import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { useNavigate } from "react-router-dom";
import { useSocket } from "../socket/SocketProvider";
import { appointmentCancel } from "../../service/videoCallService";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { sanitizeHtml } from "../../helpers/sanitizeHtml";
import {
  startRingtoneFromUserGesture,
  stopRingtone,
  subscribeRingtoneBlocked,
  isRingtonePlayBlocked,
  isIOS,
} from "../../util/ringtone";
import { stopAllCallMedia } from "../../util/stopAllCallMedia";
import { stopAllRegisteredMedia } from "../../util/mediaTrackRegistry";
import { getCallPeer } from "../../util/callPeerDisplay";
import defaultUserImage from "../../assets/images/user.svg";
import { forceAcquireCallLock } from "../../util/sessionTabLock";
import { apiRequest } from "../../util/async/apiUtils";

const subheadingLang = {
  en: "Informed Consent has been accepted and Payment has been authorized.",
  es: "Se ha aceptado el Consentimiento Informado y se ha autorizado el Pago.",
  it: "Il Consenso Informato è stato accettato e il Pagamento è stato autorizzato.",
};
const joinLang = {
  en: "Join Now",
  es: "Únete Ahora",
  it: "Iscriviti Ora",
};
const cancelLang = {
  en: "Cancel",
  es: "Cancelar",
  it: "Cancellare",
};
const tapForSoundLang = {
  en: "🔊 Tap here to enable ringtone (required on iPhone)",
  es: "🔊 Toca aquí para activar el tono (necesario en iPhone)",
  it: "🔊 Tocca qui per attivare la suoneria (necessario su iPhone)",
};

export const JoinChatModal = ({ isOpen, setIsOpen, appointmentData, callerData, audioRef, from = null }) => {
  const lang = useSelector((state) => state?.languageSlice?.data);
  const language = localStorage.getItem("language") || lang;
  const navigate = useNavigate();
  const appointment = appointmentData || JSON.parse(localStorage.getItem("appointmentData") || "null");
  const socket = useSocket();
  // const userDetails = useSelector((state) => state?.profileSlice?.data);
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const [soundBlocked, setSoundBlocked] = useState(() => isRingtonePlayBlocked());

  // Peer = other party (consultant → client; client → consultant)
  const peerInfo = getCallPeer({
    roleCode: userDetails?.roleCode,
    myId: userDetails?._id || userDetails?.id,
    appointment: appointment || callerData,
    callerData,
  });
  const displayName = peerInfo.name || "Caller";
  const displayImage = peerInfo.image || defaultUserImage;

  useEffect(() => {
    if (!isOpen) {
      setSoundBlocked(false);
      return undefined;
    }
    setSoundBlocked(isRingtonePlayBlocked());
    return subscribeRingtoneBlocked(setSoundBlocked);
  }, [isOpen]);

  const handleEnableSound = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    // Only enable sound from the explicit banner button — never from Join/Cancel clicks
    if (isRingtonePlayBlocked()) {
      startRingtoneFromUserGesture().then((ok) => {
        if (ok) setSoundBlocked(false);
      });
    }
  };

  const stopRingAndClose = () => {
    const appointmentId = appointment?.appointmentId || appointment?.id || callerData?.appointmentId;
    stopRingtone("decline", appointmentId);
    try {
      if (audioRef?.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    } catch (_) {
      /* ignore */
    }
    setIsOpen(false);
  };

  const handleJoinNow = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const appointmentId = String(
      appointment?.appointmentId ||
        appointment?.id ||
        callerData?.appointmentId ||
        "",
    );
    if (!appointmentId) {
      toast.error("Missing appointment. Please try again.");
      return;
    }
    try {
      const res = await apiRequest("get", "appointment/get-already-in-call", {
        queries: { appointmentId },
      });
      const st = String(res?.data?.data?.status || res?.data?.docs?.status || "");
      if (st === "cancel" || st === "close") {
        toast.info("This chat was cancelled.", { autoClose: 4000 });
        setIsOpen(false);
        try {
          localStorage.removeItem("appointmentData");
          localStorage.removeItem("joined");
        } catch (_) {
          /* ignore */
        }
        return;
      }
    } catch (_) {
      /* if status cannot be confirmed, still try to join */
    }
    // Stop THIS tab + broadcast to all other open tabs
    stopRingtone("accepted", appointmentId);
    try {
      if (audioRef?.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    } catch (_) {
      /* ignore */
    }

    const consultantId = String(
      appointment?.consultantId ||
        appointment?.callerData?.consultant?.id ||
        callerData?.consultantId ||
        callerData?.callerData?.consultant?.id ||
        userDetails?._id ||
        "",
    );
    const clientId = String(
      appointment?.clientId ||
        appointment?.callerData?.user?.id ||
        callerData?.clientId ||
        callerData?.callerData?.user?.id ||
        "",
    );

    // Full payload for BlankLayout / TextChat — mode chat is required (no OpenTok sessionId)
    const persist = {
      ...(appointment || {}),
      ...(callerData || {}),
      appointmentId,
      consultantId,
      clientId,
      mode: "chat",
      service:
        appointment?.service ||
        callerData?.service ||
        "direct-access-online-psychological-counseling",
      callerData:
        appointment?.callerData ||
        callerData?.callerData ||
        callerData ||
        {},
    };
    try {
      localStorage.setItem("appointmentData", JSON.stringify(persist));
      localStorage.setItem("joined", "true");
      sessionStorage.removeItem(`prontopsy_rejoin_wait_${appointmentId}`);
    } catch (_) {
      /* ignore */
    }

    const startIso = persist?.appointmentStartTime || new Date().toISOString();
    persist.appointmentStartTime = startIso;
    persist.sessionStartTime = persist?.sessionStartTime || startIso;

    if (socket && appointmentId) {
      socket.emit("joinAppointmentRoom", { appointmentId });
      socket.emit("consultantBusy", {
        consultantId,
        userId: clientId,
        isBusy: true,
        appointmentId,
      });
      socket.emit("consultantJoin", {
        joined: true,
        mode: "chat",
        data: persist.callerData,
        appointmentId,
        clientId,
        consultantId,
        userId: userDetails?._id,
      });
      socket.emit("chatJoinConfirm", {
        joined: true,
        ...persist,
        userId: userDetails?._id,
        consultantId,
        clientId,
        appointmentId,
        appointmentStartTime: startIso,
        sessionStartTime: startIso,
        data: persist.callerData,
      });
    }

    setIsOpen(false);
    stopRingtone("accepted", appointmentId);
    // Take lock for this call and drop any leftover locks from prior multi-tab paste
    if (appointmentId) forceAcquireCallLock(appointmentId);
    // Navigate after storage is set so BlankLayout does not kick to home
    navigate(`/text-chat/${appointmentId}`, { replace: true });
  };

  useSocket(
    "chatJoinConfirmResponse",
    async (data) => {
      if (
        data?.joined === true &&
        data?.consultantId === userDetails?._id &&
        callerData?.callerData?.consultant?._id === userDetails?._id
      ) {
        stopRingtone();
        setIsOpen(false);
      }
    },
    [socket, userDetails?._id, callerData, setIsOpen],
  );

  const handleJoinLater = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    stopRingAndClose();
    appointmentCancel({ appointmentId: appointment?.appointmentId });
    socket &&
      socket.emit("chatSessionReject", {
        disconnect: true,
        appointmentId: appointment?.appointmentId,
        role: userDetails?.roleCode,
        consultantId: appointment?.consultantId || callerData?.callerData?.consultant?._id,
        userId: appointment?.callerData?.user?.id || callerData?.callerData?.user?._id,
      });

    socket &&
      socket.emit("consultantBusy", {
        consultantId: appointment?.consultantId || callerData?.callerData?.consultant?._id,
        userId: appointment?.callerData?.user?.id || callerData?.callerData?.user?._id,
        isBusy: false,
      });
    setTimeout(() => {
      localStorage.removeItem("appointmentData");
    }, 1000);
  };

  useEffect(() => {
    let timer;

    if (isOpen) {
      timer = setTimeout(() => {
        handleJoinLater();
      }, 1000 * 60); // 1 minute
    }

    // Cleanup the timeout if isOpen changes or component unmounts
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useSocket(
    "chatSessionRejectResponse",
    async (data) => {
      if (!data?.disconnect) return;

      const isCurrentUserInvolved = userDetails?._id === data?.userId || userDetails?._id === data?.consultantId;
      if (!isCurrentUserInvolved) return;

      setIsOpen(false);

      if (userDetails?.roleCode !== "user" && data?.role === "user") {
        toast.info("Client has canceled the call. Stay online as they may try to reach out again.", {
          autoClose: 5000,
        });
      }
      localStorage.removeItem("appointmentData");
      localStorage.removeItem("joined");
    },
    [socket],
  );

  const headingLang = {
    en: `Want to start a <span class="text-prontopsy-blue text-xl font-bold">chat</span> consult with you.`,
    es: `Quiero iniciar una consultar de <span class="text-prontopsy-blue text-xl font-bold">chat</span> contigo.`,
    it: `voglio iniziare una consultare in <span class="text-prontopsy-blue text-xl font-bold">chat</span> con te.`,
  };

  const scheduleHeadingLang = {
    en: "want to start a <span class='text-prontopsy-blue text-xl font-bold'>chat</span> consult in booked call with you.",
    es: "quiere iniciar una consulta de <span class='text-prontopsy-blue text-xl font-bold'>chat</span> en una llamada programada contigo.",
    it: "vuole iniziare un consulto <span class='text-prontopsy-blue text-xl font-bold'>chat</span> in una chiamata prenotata con te.",
  };



  return (
    <Modal
      isOpen={isOpen}
      // setIsOpen={setIsOpen}
      titleShow={false}
      size={"xl2"}>
      <div className="flex flex-col items-center justify-center p-8 bg-white rounded-lg">
        {isIOS() && soundBlocked && (
          <button
            type="button"
            onClick={handleEnableSound}
            onTouchEnd={handleEnableSound}
            className="w-full mb-4 bg-amber-500 text-white font-bold py-3 px-4 rounded-xl shadow-lg animate-pulse text-sm md:text-base">
            {tapForSoundLang?.[language] || tapForSoundLang.en}
          </button>
        )}
        <img
          crossOrigin="anonymous"
          src={displayImage}
          alt={displayName || "Profile"}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = defaultUserImage;
          }}
          className="!w-32 !h-32 object-cover rounded-full border-4 border-gray-200 mb-6 flex items-center justify-center bg-gray-100"
        />
        <div className="text-lg font-medium text-gray-500 mb-6 capitalize text-center pb-4">
          <p className="text-prontopsy-pink text-2xl font-bold pb-0 uppercase">
            {displayName}
          </p>
          <div
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(appointment?.service === "direct-access-online-psychological-counseling"
                  ? headingLang?.[language]
                  : scheduleHeadingLang?.[language]) }}
          />
        </div>
        <span className="mt-[-25px] text-center text-prontopsy-dodgerBlue">{subheadingLang?.[language]}</span>
        <div className="flex space-x-6 text-xl mt-4">
          <button
            type="button"
            onClick={handleJoinNow}
            className="bg-green-500 flex gap-4 items-center text-white px-8 py-4 rounded-lg shadow-md hover:bg-green-600 transition duration-300 ease-in-out">
            <i className="fa-solid fa-message"></i>
            {joinLang?.[language]}
          </button>
          <button
            type="button"
            onClick={handleJoinLater}
            className="bg-red-500 flex gap-4 items-center text-white px-8 py-4 rounded-lg shadow-md hover:bg-red-600 transition duration-300 ease-in-out">
            <i className="fa-solid fa-xmark"></i>
            {cancelLang?.[language]}
          </button>
        </div>
      </div>
    </Modal>
  );
};
